import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Banknote, BarChart3, Car, Clock, DollarSign, Heart,
} from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import { SegmentedPicker, StatCard, LoadingView } from '../shared/ui';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../shared/models';
import { useDeliveredOrders, earningsRange } from '../components/earnings/useDeliveredOrders';
import { listenToPayouts, requestPayout } from '../shared/services/payoutService';
import EarningsHistorySection from '../components/earnings/EarningsHistorySection';
import DailyEarningsCalendar from '../components/earnings/DailyEarningsCalendar';
import DeliveryHistoryCard from '../components/history/DeliveryHistoryCard';

const FILTERS = ['Day', 'Week', 'Month', 'Year'];

const EARNINGS_TITLE = {
  Day: "Today's Earnings",
  Week: "This Week's Earnings",
  Month: "This Month's Earnings",
  Year: "This Year's Earnings",
};

export default function EarningsPage() {
  const { driverId } = useAuth();
  const navigate = useNavigate();
  const { orders, loading } = useDeliveredOrders(driverId);
  const [filter, setFilter] = useState('Day');
  const [payouts, setPayouts] = useState([]);
  const [requesting, setRequesting] = useState(false);

  // /payouts — driver's own payout requests + admin payment records.
  useEffect(() => {
    if (!driverId) return undefined;
    return listenToPayouts(driverId, setPayouts, () => {});
  }, [driverId]);

  // iOS computeStats — only orders that have a deliveryFee, inside the range.
  const stats = useMemo(() => {
    const [start, end] = earningsRange(filter);
    const filtered = orders.filter(o => o.hasFee && o.date >= start && o.date <= end);
    let totalEarnings = 0, totalTips = 0, tipped = 0, distance = 0;
    for (const o of filtered) {
      totalEarnings += o.totalEarnings;
      totalTips += o.tipAmount;
      if (o.tipAmount > 0) tipped += 1;
      distance += o.distance;
    }
    const count = filtered.length;
    const pendingAmount = payouts
      .filter(p => (p.paymentStatus || '').toLowerCase() === 'pending')
      .reduce((s, p) => s + p.amount, 0);
    return {
      totalEarnings,
      pendingAmount,
      deliveriesCount: count,
      hoursOnline: count * 0.5, // iOS estimates 0.5h per delivery
      averagePerOrder: count > 0 ? totalEarnings / count : 0,
      totalDistance: distance,
      totalTips,
      tipped,
    };
  }, [orders, payouts, filter]);

  const recentDeliveries = useMemo(() => orders.slice(0, 10), [orders]);

  // Available to withdraw = lifetime earnings minus paid + pending payouts.
  const availableBalance = useMemo(() => {
    const lifetime = orders.reduce((s, o) => s + o.totalEarnings, 0);
    const committed = payouts
      .filter((p) => ['pending', 'paid'].includes((p.paymentStatus || '').toLowerCase()))
      .reduce((s, p) => s + p.amount, 0);
    return Math.max(0, lifetime - committed);
  }, [orders, payouts]);

  return (
    <div>
      <ChefHeader title="Earnings" />
      {loading ? (
        <LoadingView label="Loading earnings…" />
      ) : (
        <div className="px-4 py-4 space-y-5">
          {/* Compact stat cards on top — iOS 3-column grid, two rows */}
          <div className="grid grid-cols-3 gap-2">
            <StatCard label="Earnings" value={formatCurrency(stats.totalEarnings)} icon={DollarSign} color="text-green-600" />
            <StatCard label="Deliveries" value={`${stats.deliveriesCount}`} icon={Car} color="text-red-500" />
            <StatCard label="Tips" value={formatCurrency(stats.totalTips)} icon={Heart} color="text-pink-500" />
            <StatCard label="Avg / Order" value={formatCurrency(stats.averagePerOrder)} icon={BarChart3} color="text-blue-600" />
            <StatCard label="Hours" value={stats.hoursOnline.toFixed(1)} icon={Clock} color="text-purple-600" />
            <StatCard label="Distance" value={`${stats.totalDistance.toFixed(1)} mi`} icon={Car} color="text-orange-500" />
          </div>

          {/* Earnings Overview — mirrors iOS / restaurant "Revenue Overview":
              title + totals, timeframe picker inside, live chart always visible */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Earnings Overview</h3>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-lg font-extrabold text-gray-900">{formatCurrency(stats.totalEarnings)}</p>
                  <p className="text-[11px] text-gray-400">
                    {stats.pendingAmount > 0 ? `${formatCurrency(stats.pendingAmount)} pending` : EARNINGS_TITLE[filter]}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-extrabold text-gray-900">{stats.deliveriesCount}</p>
                  <p className="text-[11px] text-gray-400">deliveries</p>
                </div>
              </div>
            </div>

            <SegmentedPicker options={FILTERS} value={filter} onChange={setFilter} />
            <div className="border-t border-gray-100" />

            <EarningsHistorySection orders={orders} timeFilter={filter} bare />
          </div>

          {/* Monthly calendar (iOS DriverDailyEarningsView) */}
          <DailyEarningsCalendar orders={orders} />

          {/* Payouts — /payouts collection: request + history (admin marks paid) */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Banknote size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-900">Payouts</h3>
              </div>
              <button
                onClick={async () => {
                  if (availableBalance <= 0 || requesting) return;
                  setRequesting(true);
                  try {
                    await requestPayout(driverId, availableBalance);
                  } catch (e) {
                    console.warn('requestPayout failed:', e);
                  } finally {
                    setRequesting(false);
                  }
                }}
                disabled={availableBalance <= 0 || requesting}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-green-600 text-white active:bg-green-700 disabled:opacity-40"
              >
                {requesting ? 'Requesting…' : 'Request Payout'}
              </button>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Available balance</span>
              <span className="font-bold text-gray-900">{formatCurrency(availableBalance)}</span>
            </div>
            <p className="text-[11px] text-gray-400">
              Payments are made outside the app by bank transfer. Make sure your
              bank details are filled in under Profile → Payout Details.
            </p>
            {payouts.length > 0 && (
              <div className="divide-y divide-gray-100 -mx-4 px-4">
                {payouts.slice(0, 10).map((p) => {
                  const st = (p.paymentStatus || 'Pending').toLowerCase();
                  const chip = st === 'paid'
                    ? 'bg-green-50 text-green-700'
                    : st === 'cancelled'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-amber-50 text-amber-700';
                  return (
                    <div key={p.id} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(p.amount)}</p>
                        <p className="text-[11px] text-gray-400">
                          {p.requestedAt?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {p.paymentReferenceNumber ? ` · Ref ${p.paymentReferenceNumber}` : ''}
                          {p.paymentNotes ? ` · ${p.paymentNotes}` : ''}
                        </p>
                      </div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${chip}`}>
                        {p.paymentStatus || 'Pending'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent deliveries */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <h3 className="font-bold text-gray-900">Recent Deliveries</h3>
              <Link to="/driver/history" className="text-sm text-blue-500 font-medium">
                View All
              </Link>
            </div>
            {recentDeliveries.length === 0 ? (
              <p className="text-sm text-gray-400 px-1">No completed deliveries yet</p>
            ) : (
              recentDeliveries.map(o => (
                <button key={o.id} onClick={() => navigate(`/driver/history/${o.id}`)} className="w-full">
                  <DeliveryHistoryCard order={o} />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
